/** @file Tab strip with roving focus (arrows, Home, End) and an optional panel for the active tab. */
import { useId, type KeyboardEvent, type ReactNode } from 'react';
import './tokens.css';
import './Tabs.css';
import { hasCommandModifier } from './logic/hasCommandModifier';
import { joinClass } from './logic/joinClass';
import { stepIndex } from './logic/stepIndex';

/** One tab; `label` is already-translated text. */
export interface TabDef {
  id: string;
  label: string;
  disabled?: boolean;
}

/** Props of Tabs. */
export interface TabsProps {
  tabs: readonly TabDef[];
  activeId: string;
  onChange: (id: string) => void;
  /** Already-translated accessible name of the tab list. */
  label: string;
  /** Content of the active tab; omit when the parent renders the content itself. */
  children?: ReactNode;
}

/** Arrow keys move to the previous or next enabled tab and activate it; Home and End jump to the first or last. Consumed keys do not reach the global shortcuts; chords with Ctrl, Cmd or Alt (browser Back, Ctrl+Home) are left alone. */
function moveTab(e: KeyboardEvent<HTMLElement>, tabs: readonly TabDef[], activeId: string, onChange: (id: string) => void): void {
  if (hasCommandModifier(e)) return;
  const disabled = tabs.map((tab) => tab.disabled === true);
  const current = tabs.findIndex((tab) => tab.id === activeId);
  const targets: Record<string, number> = {
    ArrowRight: stepIndex(current, 1, disabled, true),
    ArrowLeft: stepIndex(current, -1, disabled, true),
    Home: stepIndex(-1, 1, disabled, false),
    End: stepIndex(-1, -1, disabled, false),
  };
  const next = Object.hasOwn(targets, e.code) ? targets[e.code] : undefined;
  const tab = next === undefined ? undefined : tabs[next];
  if (!tab) return;
  e.preventDefault();
  e.stopPropagation();
  onChange(tab.id);
  e.currentTarget.querySelectorAll<HTMLElement>('[role="tab"]')[tabs.indexOf(tab)]?.focus();
}

/** Controlled tabs: `activeId` and `onChange` live in the parent. */
export function Tabs({ tabs, activeId, onChange, label, children }: TabsProps) {
  const uid = useId();
  return (
    <div className="p2-tabs">
      <div role="tablist" aria-label={label} className="p2-tablist" onKeyDown={(e) => { moveTab(e, tabs, activeId, onChange); }}>
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            id={`${uid}-tab-${tab.id}`}
            aria-selected={tab.id === activeId}
            aria-controls={`${uid}-panel`}
            tabIndex={tab.id === activeId ? 0 : -1}
            disabled={tab.disabled === true}
            className={joinClass('p2-tab', tab.id === activeId && 'p2-tab-active')}
            onClick={() => { onChange(tab.id); }}
          >
            {tab.label}
          </button>
        ))}
      </div>
      {children !== undefined && <div role="tabpanel" id={`${uid}-panel`} aria-labelledby={`${uid}-tab-${activeId}`} className="p2-tabpanel">{children}</div>}
    </div>
  );
}
